"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Checkbox,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslations } from "next-intl";
import {
  GET_PRODUCT_APPLICATION,
  IMAGES,
  WEB_COLORS,
  PRODUCT_HANDLER,
  STAGE_BASED_ROUTING,
  STAGE,
  SUBSTAGE,
  WEB_EVENTS,
  ConsentType,
} from "@/lib/constants";
import Image from "next/image";
import {
  CheckboxComponent,
  DividerComponent,
  LoaderComponent,
} from "@/components";
import {
  useConsentCreateMutation,
  useCreateBankUrlMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
  useGetOffersQuery,
} from "@/lib/redux";
import { useFormik } from "formik";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { useDispatch } from "react-redux";
import { setModalAttributes } from "@/lib/redux/slice/ModalSlice";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import { useRouter } from "@/i18n/routing";
import VerificationStep from "./verification-step";
import { sessionStorageUtil } from "@/lib/utils/common.utils";
const STEPS = [
  "verifyViaOtp",
  "chooseMultipleBankAccounts",
  "fetchingLinkedAccounts",
  "consentToCheckEligibility",
];

const COLORS = {
  primary: "#004F45",
  success: "#00D639",
  cardBackground: "#E6F1F1",
  pageBackground: "#F5FAFA",
  iconBackground: "#DDEBEB",
  bodyText: "#666A6B",
  mutedText: "#979797",
  timeline: "#87AAA7",
};

const OneMoneyLogo = () => {
  return (
    <Typography
      component="div"
      sx={{
        color: "#8D9393",
        fontSize: "10px",
        textAlign: "center",
        lineHeight: 1.5,
      }}
    >
      Powered by{" "}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "flex-end",
          justifyContent: "center",
          position: "relative",
          width: 90,
          height: 20,
        }}
      >
        <Image
          width={90}
          height={20}
          src={IMAGES.oneMoneyLogo}
          alt="Step icon"
        />
      </Box>
      <br />
      RBI regulated Account Aggregator
    </Typography>
  );
};

const InstructionsScreen = ({ product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const router = useRouter();
  const [consentChecked, setConsentChecked] = useState(false);
  const [dataProtectionModal, showDataProtectionModal] = useState(false);
  const { setActions } = useFooter();
  const dispatch = useDispatch();

  const { data: customerData, isLoading: fetchingCustomer } =
    useGetCustomerQuery();
  const { data: applicationData } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
  });
  const [
    createBankurl,
    { data: bankurlInfoData, isLoading: fetchingURL, error: createBankUrlErr },
  ] = useCreateBankUrlMutation();
  const [createConsent, { isLoading: consentDataLoading }] =
    useConsentCreateMutation();
  const productApplication = useMemo(
    () =>
      GET_PRODUCT_APPLICATION(PRODUCT_HANDLER[product], applicationData?.data),
    [applicationData?.data],
  );

  const {
    data: offersData,
    error: errorOffers,
    isLoading: isfetchingOffer,
  } = useGetOffersQuery(
    {
      appId: productApplication?.appId,
    },
    {
      skip: !(
        productApplication?.appId &&
        (productApplication?.subStage === SUBSTAGE.BRE2_AA_ACCEPT ||
          productApplication?.subStage === SUBSTAGE.BRE2_PERFIOS_ACCEPT ||
          productApplication?.subStage ===
            SUBSTAGE.PAN_BANK_NAME_MATCH_CREDIT_QUEUE ||
          productApplication?.subStage === SUBSTAGE.AA_WEB_REQUEST_URL_ACCEPT)
      ),
    },
  );

  const isLoading = isfetchingOffer || fetchingURL;

  useEffect(() => {
    setActions({
      onConfirm: handleProceed,
      showConfirm: true,
      showReject: false,
      disabledConfirmBtn: !consentChecked,
      confirmBtnLabel: "Proceed",
    });
  }, [setActions, consentChecked]);

  useEffect(() => {
    sendEventAnalytics({
      eventName: WEB_EVENTS.AA_INFO_SCREEN_VIEWED,
      ...productApplication,
      ...customerData?.data,
    });
  }, []);

  useErrorHandling(createBankUrlErr);

  useEffect(() => {
    if (
      productApplication?.stage &&
      productApplication?.stage === STAGE.OFFERS &&
      (((productApplication?.subStage === SUBSTAGE.BRE2_AA_ACCEPT ||
        productApplication?.subStage === SUBSTAGE.BRE2_PERFIOS_ACCEPT ||
        productApplication?.subStage ===
          SUBSTAGE.PAN_BANK_NAME_MATCH_CREDIT_QUEUE) &&
        !offersData?.data?.topUpOffer &&
        !offersData?.data?.bre2Popup) ||
        (productApplication?.subStage === SUBSTAGE.AA_WEB_REQUEST_URL_ACCEPT &&
          offersData?.data?.callbackInitiated === "pending"))
    ) {
      router.replace(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [
    productApplication?.stage,
    offersData?.data,
    productApplication?.subStage,
  ]);

  useEffect(() => {
    if (
      bankurlInfoData?.statusCode === 202 &&
      bankurlInfoData?.data?.webRedirectionUrl
    ) {
      window.location.href = `${bankurlInfoData?.data?.webRedirectionUrl}`;
    }
  }, [bankurlInfoData]);

  useEffect(() => {
    if (consentChecked && !dataProtectionModal) {
      showDataProtectionModal(true);
      dispatch(
        setModalAttributes({
          open: true,
          message: "",
          type: "ONE_MONEY_CONSENT",
        }),
      );
      return;
    }
  }, [consentChecked]);

  const handleProceed = async () => {
    const consent_payload = {
      customerId: customerData?.data?.id,
      appId: productApplication?.appId,
      product_type: "PL",
      type: ConsentType.BANKING,
      value: {
        account_aggregator: true,
      },
    };
    const payload = {
      appId: productApplication?.appId,
      mobile: customerData?.data?.phone,
    };
    sendEventAnalytics({
      eventName: WEB_EVENTS.AA_INFO_PROCEED_CLICKED,
      ...productApplication,
      ...customerData?.data,
    });
    try {
      await createConsent(consent_payload).unwrap();
      sessionStorageUtil.remove("bankingErrorPopup");
      await createBankurl(payload);
    } catch (error) {
      console.error("createBankurl failed:", error);
    }
  };
  return (
    <>
      <LoaderComponent open={isLoading} />
      <Box
        sx={{
          width: "100%",
          minHeight: isMobile ? "100vh" : "auto",
          bgcolor: {
            xs: "#FFFFFF",
            md: COLORS.pageBackground,
          },
          borderRadius: {
            xs: 0,
            md: "18px",
          },
          px: {
            xs: 0,
            md: 3,
          },
          pt: {
            xs: 2.25,
            md: 3,
          },
          pb: {
            xs: 2.5,
            md: 2.5,
          },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: {
              xs: "block",
            },
            mb: { xs: 2, md: 4.5 },
          }}
        >
          <Typography
            component="h1"
            sx={{
              color: { xs: WEB_COLORS.black, md: WEB_COLORS.darkTeal },
              fontWeight: { xs: "500", md: "700" },
              fontSize: { xs: "18px", md: "24px" },
              lineHeight: 1.25,
            }}
          >
            Bank Verification
          </Typography>
          <Box
            sx={{
              display: {
                xs: "block",
                md: "none",
              },
              mt: 2,
            }}
          >
            <DividerComponent />
          </Box>

          <Typography
            sx={{
              mt: 0.9,
              color: { xs: WEB_COLORS.darkTeal, md: WEB_COLORS.shadedBlack },
              fontSize: { xs: "20px", md: "16px" },
              fontWeight: { xs: 700, md: 400 },
            }}
          >
            Get your best loan offer faster
          </Typography>
        </Box>

        <Stack
          spacing={{
            xs: 2.4,
            md: 2.9,
          }}
        >
          {STEPS.map((step, index) => (
            <VerificationStep
              key={step.id}
              step={step}
              isLast={index === STEPS.length - 1}
            />
          ))}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.4}
          sx={{
            mt: {
              xs: 2.2,
              md: 1.8,
            },
            ml: {
              xs: -0.75,
              md: -0.5,
            },
          }}
        >
          <CheckboxComponent
            checked={consentChecked}
            onChange={(event) => setConsentChecked(event.target.checked)}
            // disableRipple
            checkboxProps={{
              p: 0.5,
              mt: "-3px",
              color: WEB_COLORS.darkTeal,
              "&.Mui-checked": {
                color: WEB_COLORS.darkTeal,
              },
              "& .MuiSvgIcon-root": {
                fontSize: {
                  xs: 20,
                  md: 22,
                },
              },
            }}
            label={
              <p style={{ color: COLORS.mutedText }}>
                I allow Hero FinCorp to share my mobile number with OneMoney for
                linking my bank and accept{" "}
                <a
                  href="https://www.onemoney.in/tandc.html"
                  className="doc-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLORS.mutedText }}
                >
                  OneMoney T & C.
                </a>
              </p>
            }
          />
        </Stack>

        <Box
          sx={{
            display: {
              xs: "block",
              md: "none",
            },
            mt: 2.7,
          }}
        >
          <OneMoneyLogo />
        </Box>
      </Box>
      <Box
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
          mt: 2.7,
        }}
      >
        <OneMoneyLogo />
      </Box>
    </>
  );
};
export default InstructionsScreen;
